<template>
  <div>
    <MainNavbar />
    <div class="tennis-dashboard-page">
      <div class="page-header">
        <h1>Tennis Player Dashboard</h1>
        <p v-if="currentTeam?.name">{{ currentTeam.name }} - {{ formatLabel(currentTeam.match_format) || "Singles/Doubles" }}</p>
      </div>

      <div class="actions-row">
        <button @click="$router.push({ name: 'TennisOrganizeMatch' })">Browse Players/Pairs</button>
        <button @click="refreshChallenges" :disabled="loadingChallenges">Refresh Challenges</button>
      </div>

      <section class="card">
        <h3>Pending Challenges</h3>
        <div v-if="loadingChallenges">Loading...</div>
        <div v-else-if="!challenges.length" class="muted">No pending challenges.</div>
        <div v-for="challenge in challenges" :key="challenge.id || challenge.match_id" class="challenge-row">
          <div>
            <strong>{{ challenge.opponent_name || "Opponent Player/Pair" }}</strong>
            <div class="muted">{{ formatDate(challenge.date || challenge.scheduled_at) }} • {{ challenge.match_format || "-" }}</div>
          </div>
          <div class="btn-group">
            <button @click="acceptChallenge(challenge.id || challenge.match_id)" :disabled="processingChallenge === (challenge.id || challenge.match_id)">Accept</button>
            <button @click="rejectChallenge(challenge.id || challenge.match_id)" :disabled="processingChallenge === (challenge.id || challenge.match_id)">Reject</button>
          </div>
        </div>
      </section>

      <section class="card">
        <h3>Upcoming Matches</h3>
        <div v-if="!upcomingMatches.length" class="muted">No upcoming matches.</div>
        <div v-for="match in upcomingMatches" :key="match.id || match.match_id" class="challenge-row">
          <div>
            <strong>{{ match.opponent_name || "Opponent Player/Pair" }}</strong>
            <div class="muted">{{ formatDate(match.date || match.scheduled_at) }} • {{ match.status || "pending" }}</div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import MainNavbar from "@/components/main/headernfooter/header.vue";
import { buildApiUrl } from "@/config/api";

export default {
  name: "TennisTeamDashboard",
  components: { MainNavbar },
  data() {
    return {
      currentTeam: null,
      teamMatches: [],
      upcomingMatches: [],
      challenges: [],
      loadingChallenges: false,
      processingChallenge: null,
    };
  },
  async mounted() {
    await this.loadTeamData();
  },
  methods: {
    getAuthToken() {
      return localStorage.getItem("access_token") || localStorage.getItem("token") || sessionStorage.getItem("access_token") || sessionStorage.getItem("token");
    },
    formatLabel(value) {
      const map = { singles: "Singles", doubles: "Doubles" };
      return map[value] || value || "";
    },
    formatDate(value) {
      if (!value) return "N/A";
      return new Date(value).toLocaleString();
    },
    isPastMatch(match) {
      const raw = match?.date || match?.scheduled_at;
      if (!raw) return false;
      return new Date(raw).getTime() < Date.now();
    },
    async loadTeamData() {
      const token = this.getAuthToken();
      if (!token) return;
      const response = await fetch(buildApiUrl("/api/teams/tennis/my-team?include_members=true"), {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (response.status === 404) {
        this.$router.replace({ name: "TennisTeamRegistration" });
        return;
      }
      if (!response.ok) return;
      const teamData = await response.json();
      this.currentTeam = teamData.team || teamData;
      await this.loadTeamMatches();
      await this.loadChallenges();
    },
    async loadTeamMatches() {
      if (!this.currentTeam?.team_id && !this.currentTeam?.team_id_num) return;
      const teamId = this.currentTeam.team_id || this.currentTeam.team_id_num;
      const token = this.getAuthToken();
      const response = await fetch(buildApiUrl(`/api/matches/tennis/teams/${teamId}/matches`), {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (!response.ok) return;
      const data = await response.json();
      this.teamMatches = data.matches || [];
      this.upcomingMatches = this.teamMatches.filter((m) => !this.isPastMatch(m));
    },
    async loadChallenges() {
      this.loadingChallenges = true;
      try {
        if (!this.currentTeam?.team_id && !this.currentTeam?.team_id_num) return;
        const teamId = this.currentTeam.team_id || this.currentTeam.team_id_num;
        const token = this.getAuthToken();
        const response = await fetch(buildApiUrl(`/api/matches/tennis/teams/${teamId}/challenges`), {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        if (!response.ok) return;
        const data = await response.json();
        this.challenges = data.challenges || [];
      } finally {
        this.loadingChallenges = false;
      }
    },
    async refreshChallenges() {
      await this.loadChallenges();
    },
    async acceptChallenge(challengeId) {
      this.processingChallenge = challengeId;
      try {
        const token = this.getAuthToken();
        const response = await fetch(buildApiUrl(`/api/matches/tennis/${challengeId}/accept`), {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        if (!response.ok) return;
        this.challenges = this.challenges.filter((c) => (c.id || c.match_id) !== challengeId);
        await this.loadTeamMatches();
      } finally {
        this.processingChallenge = null;
      }
    },
    async rejectChallenge(challengeId) {
      this.processingChallenge = challengeId;
      try {
        const token = this.getAuthToken();
        const response = await fetch(buildApiUrl(`/api/matches/tennis/${challengeId}/reject`), {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        if (!response.ok) return;
        this.challenges = this.challenges.filter((c) => (c.id || c.match_id) !== challengeId);
      } finally {
        this.processingChallenge = null;
      }
    },
  },
};
</script>

<style scoped>
.tennis-dashboard-page { min-height: 100vh; padding: 20px; padding-top: 80px; color: var(--text-color1); max-width: 920px; margin: 0 auto; }
.page-header { background: linear-gradient(90deg, #1f1b3a 0%, #312e81 70%); border: 1px solid #4f46e5; border-left: 4px solid #a78bfa; border-radius: 12px; padding: 20px; margin-bottom: 16px; }
.page-header h1 { color: #fff; margin: 0 0 6px; }
.page-header p { color: #ede9fe; margin: 0; }
.actions-row { display: flex; gap: 10px; margin-bottom: 12px; }
.card { background: var(--background-color1); border: 1px solid var(--background-color3); border-radius: 12px; padding: 14px; margin-bottom: 12px; }
.challenge-row { display: flex; justify-content: space-between; gap: 10px; align-items: center; padding: 10px 0; border-bottom: 1px solid var(--background-color3); }
.challenge-row:last-child { border-bottom: none; }
.muted { color: var(--text-color3); font-size: 0.9rem; }
.btn-group { display: flex; gap: 8px; }
button { border: none; border-radius: 8px; padding: 8px 12px; cursor: pointer; background: #a78bfa; color: #312e81; font-weight: 600; }
</style>
